import type { ConversationAttributeItem } from '@/entities/attributes';
import type { ConversationAttrValueInput } from '@/entities/conversations';
import {
  type AttributeValueDraft,
  createEmptyAttributeValue,
  getAttributeValueKind,
  toAttributeInputValue,
} from '@/features/attribute-filters';
import type { TimeIntervalFormat } from '@/shared/lib';

/** Свойство набора значений «сотрудник» */
export const EMPLOYEE_PROPERTY = 'employee';

/** Свойство набора значений «клиент»: имя вводится текстом, а не выбирается из клиентов системы */
export const CLIENT_PROPERTY = 'client';

/** Свойство набора значений «время звонка» */
export const CALL_TIME_PROPERTY = 'callTime';

/** Начало ключа свойства-атрибута: путь атрибута за ним не совпадёт с ключом поля записи */
const ATTRIBUTE_PROPERTY_PREFIX = 'attribute:';

/** Строка набора значений: свойство записи и заданное ему значение */
export type RecordPropertyEntry = {
  /**
   * Ключ свойства: {@link EMPLOYEE_PROPERTY}, {@link CLIENT_PROPERTY}, {@link CALL_TIME_PROPERTY} либо ключ
   * атрибута ({@link toAttributeProperty})
   */
  property: string;
  /** Значение в том виде, в каком его держит поле ввода */
  value: AttributeValueDraft;
};

/** Форма набора значений блока загрузки */
export type RecordValueSetForm = {
  /** Свойства набора в порядке добавления */
  properties: RecordPropertyEntry[];
};

/** Имя поля значения строки набора в форме */
export type RecordPropertyValueName = `properties.${number}.value`;

/** Набор значений в формате запросов создания записи и проставления значений отмеченным записям */
export type RecordValues = {
  /** Сотрудник записи */
  employeeId?: number;
  /** Имя клиента записи без пробелов по краям */
  clientName?: string;
  /** Время звонка, ISO 8601 */
  startedAt?: string;
  /** Значения атрибутов */
  attributes: ConversationAttrValueInput[];
};

/** Ключ свойства набора для атрибута */
export const toAttributeProperty = (path: string): string => `${ATTRIBUTE_PROPERTY_PREFIX}${path}`;

/** Атрибут свойства набора; `undefined` — свойство не атрибут либо атрибута нет среди доступных набору */
export const getPropertyAttribute = (
  property: string,
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
): ConversationAttributeItem | undefined =>
  property.startsWith(ATTRIBUTE_PROPERTY_PREFIX)
    ? attributesByPath.get(property.slice(ATTRIBUTE_PROPERTY_PREFIX.length))
    : undefined;

/** Пустое значение нового свойства набора — под поле, которым оно вводится */
export const createEmptyPropertyValue = (
  property: string,
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeValueDraft => {
  const attribute = getPropertyAttribute(property, attributesByPath);
  const kind = attribute ? getAttributeValueKind(attribute, timeDisplayFormat) : null;
  return kind ? createEmptyAttributeValue(kind) : '';
};

/**
 * Набор значений в формате запросов; `null` — у какого-то свойства значение не задано или не
 * читается по виду поля, и отправлять набор нельзя.
 */
export const buildRecordValues = (
  entries: RecordPropertyEntry[],
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
  timeDisplayFormat: TimeIntervalFormat,
): RecordValues | null => {
  const values: RecordValues = { attributes: [] };

  for (const { property, value } of entries) {
    if (property === EMPLOYEE_PROPERTY) {
      if (typeof value !== 'number') return null;
      values.employeeId = value;
      continue;
    }
    if (property === CLIENT_PROPERTY) {
      // Имя из одних пробелов значением не считается
      const clientName = typeof value === 'string' ? value.trim() : '';
      if (clientName === '') return null;
      values.clientName = clientName;
      continue;
    }
    if (property === CALL_TIME_PROPERTY) {
      if (typeof value !== 'string' || value === '') return null;
      values.startedAt = value;
      continue;
    }

    const attribute = getPropertyAttribute(property, attributesByPath);
    const kind = attribute ? getAttributeValueKind(attribute, timeDisplayFormat) : null;
    const inputValue = kind ? toAttributeInputValue(kind, value) : undefined;
    if (!attribute || inputValue === undefined) return null;
    values.attributes.push({ path: attribute.path, value: inputValue });
  }

  return values;
};
